import { useState } from "react";
import { Send, MessageCircle, ExternalLink, CheckCheck, Eye } from "lucide-react";
import { cn } from "@/lib/utils";

export interface PostPreviewSimulatorProps {
  readonly bodyText: string;
  readonly imageUrl?: string | undefined;
  readonly affiliateUrl?: string | undefined;
  readonly className?: string | undefined;
}

export function PostPreviewSimulator({
  bodyText,
  imageUrl,
  affiliateUrl,
  className,
}: PostPreviewSimulatorProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<"telegram" | "whatsapp">("telegram");

  const effectiveText = bodyText.trim() || "Sua mensagem aparecerá aqui em tempo real conforme você digita...";
  const isPlaceholder = !bodyText.trim();

  // Telegram photo caption limit is 1024 characters; text-only is 4096.
  const isTgCaptionLimitExceeded = Boolean(imageUrl) && bodyText.length > 1024;

  return (
    <div className={cn("flex flex-col rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] overflow-hidden shadow-sm", className)}>
      {/* Header com Abas */}
      <div className="flex items-center justify-between border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-raised))] px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Eye size={14} className="text-[hsl(var(--gold))]" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Simulador de Post
          </span>
        </div>

        <div className="flex items-center gap-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-0.5">
          <button
            type="button"
            onClick={() => setActiveTab("telegram")}
            className={cn(
              "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all",
              activeTab === "telegram"
                ? "bg-[#2AABEE] text-white shadow-xs"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Send size={12} />
            Telegram
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("whatsapp")}
            className={cn(
              "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all",
              activeTab === "whatsapp"
                ? "bg-[#25D366] text-slate-900 font-semibold shadow-xs"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <MessageCircle size={12} />
            WhatsApp
          </button>
        </div>
      </div>

      {/* Conteúdo do Simulador */}
      <div className="p-4 flex-1 flex flex-col justify-start">
        {activeTab === "telegram" ? (
          /* TELEGRAM MOCKUP */
          <div className="flex flex-col gap-2 rounded-xl bg-[#17212B] p-3.5 text-[#F5F5F5] font-sans border border-[#232E3C] shadow-md max-w-sm mx-auto w-full">
            {/* Imagem do Telegram */}
            {imageUrl ? (
              <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-[#0E1621] border border-[#2B394A]">
                <img
                  src={imageUrl}
                  alt="Pré-visualização"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLElement).style.display = "none";
                  }}
                />
              </div>
            ) : null}

            {/* Texto da Mensagem */}
            <div
              className={cn(
                "whitespace-pre-wrap text-sm leading-relaxed break-words",
                isPlaceholder && "italic text-[#7E8F9F]",
              )}
            >
              {effectiveText}
            </div>

            {/* Botão Inline estilo Telegram se houver link */}
            {affiliateUrl && (
              <div className="mt-1 pt-1.5 border-t border-[#232E3C]">
                <div className="flex items-center justify-center gap-1.5 rounded-md bg-[#2B394A] py-1.5 text-xs font-medium text-[#6CB5F9] hover:bg-[#344458] transition-colors cursor-pointer">
                  <span>Ver Oferta na Loja</span>
                  <ExternalLink size={12} />
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-1 text-[11px] text-[#7E8F9F] pt-0.5">
              <span>Agora</span>
            </div>
          </div>
        ) : (
          /* WHATSAPP MOCKUP */
          <div className="flex flex-col gap-2 rounded-xl bg-[#005C4B] p-3 text-[#E9EDEF] font-sans border border-[#004739] shadow-md max-w-sm mx-auto w-full relative">
            {/* Imagem do WhatsApp */}
            {imageUrl ? (
              <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-[#004739] border border-[#00382D]">
                <img
                  src={imageUrl}
                  alt="Pré-visualização"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLElement).style.display = "none";
                  }}
                />
              </div>
            ) : null}

            {/* Texto do WhatsApp */}
            <div
              className={cn(
                "whitespace-pre-wrap text-sm leading-relaxed break-words",
                isPlaceholder && "italic text-[#99C5BD]",
              )}
            >
              {effectiveText}
            </div>

            {affiliateUrl && (
              <div className="rounded bg-[#004E3F] p-2 text-xs text-[#53BDEB] truncate flex items-center gap-1.5">
                <ExternalLink size={12} className="shrink-0" />
                <span className="truncate">{affiliateUrl}</span>
              </div>
            )}

            <div className="flex items-center justify-end gap-1 text-[11px] text-[#99C5BD] pt-0.5">
              <span>Agora</span>
              <CheckCheck size={14} className="text-[#53BDEB]" />
            </div>
          </div>
        )}

        {/* Avisos de Validação */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[hsl(var(--border))] pt-2.5 text-[11px] text-muted-foreground">
          <span>{activeTab === "telegram" ? "Visualização Telegram" : "Visualização WhatsApp"}</span>
          {isTgCaptionLimitExceeded && activeTab === "telegram" ? (
            <span className="text-amber-400 font-medium">
              ⚠️ Legenda com imagem acima de 1.024 caracteres no Telegram.
            </span>
          ) : (
            <span className="text-emerald-500 flex items-center gap-1">
              ✓ Formatação compatível
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
